import React from 'react';
import { Checkbox, Group, Table, Text } from '@mantine/core';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import { IAppState } from '../../../../lib/types/AppState/index';
import { IDataCut, IDirection, IFormattedMatch, IOrgJob, ISelectedMatch, ISourceMatch, ISubmitMatchesResponse } from '../PropagateMatchesTypes';
import { getAllAvailablePropagateRows } from '../helpers';

type Props = {
  activeTab: string;
  closeLoader: () => void

  fetchSurveyJobMatchData: (params: {
    resetSelectedMatches?: boolean;
    surveyJobMatch?: ISourceMatch;
    matchesMade?: ISubmitMatchesResponse;
  }) => void;
  openLoader: () => void,
  orgJobs: IOrgJob[];
  setSurveyJobMatches: React.Dispatch<React.SetStateAction<ISourceMatch[]>>;
  surveyJobMatch: ISourceMatch;
  surveyJobMatches: ISourceMatch[];
  updateSurveyJobMatch: (surveyJobMatch: ISourceMatch, updates: Partial<ISourceMatch>) => ISourceMatch[];
};

const TableHeaders: React.FC<Props> = ({
  activeTab,
  fetchSurveyJobMatchData,
  orgJobs,
  setSurveyJobMatches,
  surveyJobMatch,
  surveyJobMatches,
  updateSurveyJobMatch,
  openLoader,
  closeLoader
}) => {
  const benchmarkYear = useSelector((state: IAppState) => state.session.benchmarkYear);
  const headers = [
    { label: 'Survey Job Code', property: 'survey_job_code' },
    { label: 'Survey Job Title', property: 'survey_job_title' },
    { label: 'Job Code Key', property: 'org_job_code_key' },
    { label: 'Survey Code', property: 'survey_code' },
    { label: 'Survey Title', property: 'survey_title' },
    { label: 'Currency', property: 'target_currency' },
  ];
  const masterAccountID = useSelector((state: IAppState) => state.session.organization.masterAccountId);

  const getNonExistentMatchesWithData = (surveyJobMatch: ISourceMatch) => {
    const filteredPropagateMatches = surveyJobMatch.propagateMatches.filter((propagateMatch: IFormattedMatch) => {
      const matchingSelectedRow = surveyJobMatch.selectedPropagateRows.find(
        (selectedRow: ISelectedMatch) =>
          selectedRow.targetJobId === propagateMatch.targetJobId &&
          !selectedRow.matchExists &&
          propagateMatch.dataCuts.some((cut: IDataCut) => cut.cutId === selectedRow.cutId)
      );

      return matchingSelectedRow && propagateMatch.hasData;
    });

    return filteredPropagateMatches.every((propagateMatch: IFormattedMatch) =>
      surveyJobMatch.selectedPropagateRows.some(
        (selectedRow: ISelectedMatch) =>
          selectedRow.targetJobId === propagateMatch.targetJobId &&
          propagateMatch.dataCuts.some((cut: IDataCut) => cut.cutId === selectedRow.cutId) &&
          selectedRow.isChecked === true
      )
    );
  };

  const handleSort = async (property: string) => {
    const sortDirection: IDirection = surveyJobMatch.sort?.direction === 'ASC' ? 'DESC' : 'ASC';
    const updatedMatches = updateSurveyJobMatch(surveyJobMatch, {
      sort: { property, direction: sortDirection },
    });

    setSurveyJobMatches(updatedMatches);

    const match = updatedMatches.find(match => match.sourceMatchId === surveyJobMatch.sourceMatchId);
    if (match) {
      openLoader();
      await fetchSurveyJobMatchData({surveyJobMatch: match});
      closeLoader();
    }
  };

  const isDisabled = getAllAvailablePropagateRows(surveyJobMatch, false).length === 0;

  const updateAllSelectedPropagateMatches = (
    event: React.ChangeEvent<HTMLInputElement>,
    surveyJobMatch: ISourceMatch
  ) => {
    const isChecked = event.target.checked;
    const availablePropagateRows = getAllAvailablePropagateRows(surveyJobMatch);

    setSurveyJobMatches((prevSurveyJobMatches: ISourceMatch[]) =>
      prevSurveyJobMatches.map((match: ISourceMatch) => {
        if (match.sourceMatchId !== surveyJobMatch.sourceMatchId) return match;

        const updatedSelectedRows = match.selectedPropagateRows.map((selectedRow: ISelectedMatch) => {
          const matchingAvailableRow = availablePropagateRows.find(
            (availableRow: ISelectedMatch) =>
              selectedRow.cutId === availableRow.cutId &&
              selectedRow.targetJobId === availableRow.targetJobId &&
              selectedRow.surveyCode === availableRow.surveyCode
          );

          return matchingAvailableRow ? { ...selectedRow, isChecked } : selectedRow;
        });

        return {
          ...match,
          selectedPropagateRows: updatedSelectedRows,
        };
      })
    );
  };

  return (
    <Table.Thead>
      <Table.Tr>
        {headers.map(({ label, property }, index) => (
          <Table.Th pl={index === 0 ? '20' : undefined} key={property} onClick={() => handleSort(property)}>
            <Group>
              <Text fw={700} size="sm" style={{ whiteSpace: 'nowrap' }}>
                {label}
              </Text>
              <i
                className={classnames({
                  'icon-down-open': surveyJobMatch.sort?.direction === 'DESC',
                  'icon-up-open': surveyJobMatch.sort?.direction === 'ASC',
                  'sort-icon__visible': surveyJobMatch.sort?.property === property,
                  'sort-icon__hidden': surveyJobMatch.sort?.property !== property,
                })}
              />
            </Group>
          </Table.Th>
        ))}
        <Table.Th>
          <Group>
            <Text fw={700}>Data Cut</Text>
          </Group>
        </Table.Th>
        <Table.Th w={100}>
          <Group justify="center">
            <Checkbox
              disabled={isDisabled}
              checked={!isDisabled && getNonExistentMatchesWithData(surveyJobMatch)}
              onChange={event => updateAllSelectedPropagateMatches(event, surveyJobMatch)}
              size="md"
            />
          </Group>
        </Table.Th>
      </Table.Tr>
    </Table.Thead>
  );
};

export default TableHeaders;
